import React, { useState } from 'react';

interface Task {
  id: string;
  number: number;
  title: string;
  description: string;
  requirements: Array<{
    text: string;
    subitems?: string[];
  }>;
  verification: string[];
}

const tasks: Task[] = [
  {
    id: 'task-1',
    number: 1,
    title: 'Assess Current Storage Configuration',
    description: 'Perform comprehensive analysis of the existing application and storage setup to understand current usage patterns and migration requirements.',
    requirements: [
      {
        text: 'Update the storage analysis file at /root/storage_analysis.txt with current data'
      },
      {
        text: 'Check current disk space usage with df -h and du commands'
      },
      {
        text: 'Analyze application data patterns in /var/app-storage'
      },
      {
        text: 'Document mount points and filesystem information'
      },
      {
        text: 'Review application configuration in /app/config/database.conf'
      }
    ],
    verification: [
      'The file /root/storage_analysis.txt has been updated with fresh analysis',
      'You have identified that /var/app-storage is at 70-80% capacity',
      'You understand which services depend on the storage (nginx, mysql, app-monitor)',
      'Mount points and filesystem types are documented',
      'You have a clear picture of what needs to be migrated'
    ]
  },
  {
    id: 'task-2',
    number: 2,
    title: 'Plan Migration Strategy',
    description: 'Create a comprehensive migration plan that ensures zero downtime and includes rollback procedures.',
    requirements: [
      {
        text: 'Create /root/migration_plan.txt with migration phases and timeline'
      },
      {
        text: 'Include Phase 1 through Phase 5 in your plan document'
      },
      {
        text: 'Define acceptable downtime limits (< 5 minutes)'
      },
      {
        text: 'Document rollback trigger points and procedures'
      },
      {
        text: 'Plan data integrity verification methods'
      }
    ],
    verification: [
      'The file /root/migration_plan.txt exists with complete migration plan',
      'Plan includes all 5 phases with specific timelines',
      'Rollback procedures are clearly documented',
      'Data integrity verification strategy is defined',
      'Timeline shows total downtime will be under 5 minutes'
    ]
  },
  {
    id: 'task-3',
    number: 3,
    title: 'Prepare New Storage Volume',
    description: 'Create and configure the new storage location at /mnt/new-app-storage with proper structure and permissions.',
    requirements: [
      {
        text: 'Create directory /mnt/new-app-storage as the new storage location'
      },
      {
        text: 'Create subdirectories: files, cache, and database'
      },
      {
        text: 'Set ownership to www-data:www-data for web server access'
      },
      {
        text: 'Set permissions to 755 for proper access control'
      },
      {
        text: 'Verify write access to the new storage location'
      }
    ],
    verification: [
      'Directory /mnt/new-app-storage exists with correct structure',
      'Subdirectories /mnt/new-app-storage/{files,cache,database} are created',
      'Ownership is set to www-data:www-data',
      'Permissions are set to 755',
      'Write access is confirmed for the new location'
    ]
  },
  {
    id: 'task-4',
    number: 4,
    title: 'Implement Live Data Migration',
    description: 'Execute data migration using rsync to copy all files from /var/app-storage to /mnt/new-app-storage with zero data loss.',
    requirements: [
      {
        text: 'Create /root/migration_log.txt to track migration progress'
      },
      {
        text: 'Perform initial bulk sync with rsync -avP --delete'
      },
      {
        text: 'Verify exactly 5 files are copied to new location'
      },
      {
        text: 'Execute incremental syncs to minimize final cutover time'
      },
      {
        text: 'Monitor system performance during migration'
      }
    ],
    verification: [
      'File /root/migration_log.txt exists with migration timestamps',
      'All files successfully synced from /var/app-storage to /mnt/new-app-storage',
      'Exactly 5 files present in /mnt/new-app-storage after sync',
      'Multiple incremental syncs completed and logged',
      'System load remained under 2.0 during migration'
    ]
  },
  {
    id: 'task-5',
    number: 5,
    title: 'Update Application Configuration',
    description: 'Reconfigure all application components to use the new storage location at /mnt/new-app-storage.',
    requirements: [
      {
        text: 'Update /app/config/database.conf to use /mnt/new-app-storage paths'
      },
      {
        text: 'Update /etc/nginx/sites-available/app to serve from /mnt/new-app-storage/files and report /mnt/new-app-storage in /api/status'
      },
      {
        text: 'Create /app/config/database.conf.backup, /etc/nginx/sites-available/app.backup, and /app/monitor.sh.backup'
      },
      {
        text: 'Update /app/monitor.sh so it checks /mnt/new-app-storage'
      },
      {
        text: 'Perform final data sync before configuration switchover'
      },
      {
        text: 'Restart services (nginx and app-monitor) with new configuration'
      }
    ],
    verification: [
      'File /app/config/database.conf contains /mnt/new-app-storage paths',
      'Nginx configuration points to /mnt/new-app-storage/files and the status response names /mnt/new-app-storage',
      'Backup files exist for database.conf, nginx app config, and monitor.sh',
      'File /app/monitor.sh contains /mnt/new-app-storage',
      'Services nginx and app-monitor are running',
      'Application responds at http://localhost/api/status with the new storage path'
    ]
  },
  {
    id: 'task-6',
    number: 6,
    title: 'Verify Data Integrity',
    description: 'Perform comprehensive verification to ensure all data was migrated correctly with no corruption or loss.',
    requirements: [
      {
        text: 'Generate MD5 checksums for old storage in /root/old_checksums.md5'
      },
      {
        text: 'Generate MD5 checksums for new storage in /root/new_checksums.md5'
      },
      {
        text: 'Create /root/file_count_check.txt with file count verification'
      },
      {
        text: 'Verify index.html exists at /mnt/new-app-storage/files/index.html'
      },
      {
        text: 'Test application functionality with new storage location'
      }
    ],
    verification: [
      'Files /root/old_checksums.md5 and /root/new_checksums.md5 exist',
      'Checksums match between old and new storage locations',
      'File /root/file_count_check.txt confirms 5 files in both locations',
      'File /mnt/new-app-storage/files/index.html is accessible',
      'Application is fully functional with new storage'
    ]
  },
  {
    id: 'task-7',
    number: 7,
    title: 'Document Migration Procedures',
    description: 'Create comprehensive documentation of the entire migration process for future reference and knowledge sharing.',
    requirements: [
      {
        text: 'Create /root/migration_documentation.md with complete migration report'
      },
      {
        text: 'Include migration summary, steps performed, and lessons learned'
      },
      {
        text: 'Create /root/migration_checklist.txt with reusable checklist'
      },
      {
        text: 'Document rollback procedures and validation steps'
      },
      {
        text: 'Record performance metrics and optimization recommendations'
      }
    ],
    verification: [
      'File /root/migration_documentation.md exists with full documentation',
      'Documentation includes all migration phases and outcomes',
      'File /root/migration_checklist.txt contains complete checklist',
      'Rollback procedures are clearly documented',
      'Performance analysis and recommendations are included'
    ]
  }
];

export default function StorageMigrationScalingInstructions() {
  const [currentTaskIndex, setCurrentTaskIndex] = useState(0);
  const [completedChecks, setCompletedChecks] = useState<Set<string>>(new Set());
  const [completedTasks, setCompletedTasks] = useState<Set<number>>(new Set());

  const handleTaskClick = (index: number) => {
    setCurrentTaskIndex(index);
  };

  const handleNextTask = () => {
    if (currentTaskIndex < tasks.length - 1) {
      setCurrentTaskIndex(currentTaskIndex + 1);
    } else {
      setCurrentTaskIndex(0);
    }
  };

  const handlePreviousTask = () => {
    if (currentTaskIndex > 0) {
      setCurrentTaskIndex(currentTaskIndex - 1);
    }
  };

  const toggleVerification = (taskId: string, checkIndex: number) => {
    const checkId = `${taskId}-check-${checkIndex}`;
    const next = new Set(completedChecks);
    next.has(checkId) ? next.delete(checkId) : next.add(checkId);
    setCompletedChecks(next);

    const currentTask = tasks[currentTaskIndex];
    const allDone = currentTask.verification.every((_, idx) => next.has(`${currentTask.id}-check-${idx}`));
    if (allDone && !completedTasks.has(currentTaskIndex)) {
      const t = new Set(completedTasks);
      t.add(currentTaskIndex);
      setCompletedTasks(t);
    }
  };

  const currentTask = tasks[currentTaskIndex];
  const progressPercentage = ((currentTaskIndex + 1) / tasks.length) * 100;

  return (
    <div className="bg-[#232937] rounded-xl overflow-hidden">
      {/* Header */}
      <div className="bg-gradient-to-r from-[#10b981] to-[#059669] px-8 py-6">
        <h2 className="text-2xl font-semibold text-[#1a1f2e]">Instructions</h2>
      </div>

      {/* Content Area - Increased to 1800px for less scrolling */}
      <div className="flex h-[1800px]">
        {/* Sidebar Navigation */}
        <div className="w-[280px] bg-[#1a1f2e] border-r border-[#2d3748] overflow-y-auto">
          {tasks.map((task, index) => (
            <div
              key={task.id}
              onClick={() => handleTaskClick(index)}
              className={`flex items-center px-5 py-4 cursor-pointer border-b border-[#2d3748] hover:bg-[#2d3748] transition-all relative ${
                index === currentTaskIndex ? 'bg-[#2d3748] border-l-[3px] border-l-[#10b981] pl-[17px]' : ''
              }`}
            >
              <div className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-semibold mr-3 flex-shrink-0 ${
                completedTasks.has(index) ? 'bg-[#059669]' : 'bg-[#10b981]'
              } text-[#1a1f2e]`}>
                {task.number}
              </div>
              <div className={`text-sm ${index === currentTaskIndex ? 'text-[#e2e8f0] font-medium' : 'text-[#cbd5e1]'}`}>
                {task.title}
              </div>
              {completedTasks.has(index) && (
                <span className="absolute right-5 text-[#10b981] font-bold text-sm">✓</span>
              )}
            </div>
          ))}
        </div>

        {/* Main Content */}
        <div className="flex-1 p-8 bg-[#1e293b] overflow-y-auto">
          <h3 className="text-[22px] font-semibold text-[#e2e8f0] mb-4">
            Task {currentTask.number}: {currentTask.title}
          </h3>
          <p className="text-[#94a3b8] mb-8 leading-[1.6] text-[15px]">
            {currentTask.description}
          </p>

          {/* Requirements Section */}
          <div className="mb-8">
            <h4 className="text-[#10b981] text-xs uppercase tracking-[1px] mb-4 font-semibold flex items-center">
              <span className="w-5 h-[2px] bg-[#10b981] mr-[10px]"></span>
              What You Need To Do
            </h4>

            {currentTask.requirements.map((requirement, reqIndex) => (
              <div key={reqIndex} className="bg-[#232937] border border-[#2d3748] rounded-lg p-4 mb-3 hover:border-[#374151] hover:shadow-md transition-all">
                <div className="text-[#e2e8f0] text-sm leading-[1.8]">
                  <span className="font-medium">{reqIndex + 1}. {requirement.text}</span>
                  {requirement.subitems && (
                    <ul className="mt-2 ml-5 space-y-1">
                      {requirement.subitems.map((item, idx) => (
                        <li key={idx} className="flex items-start">
                          <span className="text-[#10b981] mr-2 mt-0.5">•</span>
                          <span className="text-[#cbd5e1]">{item}</span>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              </div>
            ))}
          </div>

          {/* Verification Section */}
          <div className="bg-[#232937] border-2 border-dashed border-[#475569] rounded-lg p-5 mt-6">
            <div className="flex items-center text-[#10b981] font-semibold mb-4 text-base">
              <span className="w-5 h-5 bg-[#10b981] text-[#1a1f2e] rounded-full flex items-center justify-center text-xs mr-[10px]">
                ✓
              </span>
              Verification
            </div>

            {currentTask.verification.map((check, idx) => (
              <div
                key={idx}
                onClick={() => toggleVerification(currentTask.id, idx)}
                className="flex items-center p-[10px_12px] bg-[#1a1f2e] rounded-md mb-2 cursor-pointer hover:bg-[#1e293b] hover:translate-x-[3px] transition-all last:mb-0"
              >
                <div className={`w-[18px] h-[18px] border-2 border-[#10b981] rounded flex items-center justify-center mr-3 flex-shrink-0 transition-all ${
                  completedChecks.has(`${currentTask.id}-check-${idx}`) ? 'bg-[#10b981]' : ''
                }`}>
                  {completedChecks.has(`${currentTask.id}-check-${idx}`) && (
                    <span className="text-[#1a1f2e] text-xs font-bold">✓</span>
                  )}
                </div>
                <span className="text-[#cbd5e1] text-sm">{check}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Progress Footer */}
      <div className="flex items-center justify-between px-6 py-4 bg-[#1a1f2e] border-t border-[#2d3748]">
        <div className="text-[13px] text-[#94a3b8]">
          Progress: Task {currentTaskIndex + 1} of {tasks.length}
        </div>

        <div className="w-[200px] h-[6px] bg-[#2d3748] rounded-[3px] overflow-hidden">
          <div
            className="h-full bg-gradient-to-r from-[#10b981] to-[#34d399] transition-all duration-500 rounded-[3px]"
            style={{ width: `${progressPercentage}%` }}
          />
        </div>

        <div className="flex gap-[10px]">
          <button
            onClick={handlePreviousTask}
            disabled={currentTaskIndex === 0}
            className="px-4 py-2 bg-[#2d3748] border border-[#374151] text-[#cbd5e1] rounded-md text-[13px] cursor-pointer transition-all hover:bg-[#374151] hover:text-[#e2e8f0] disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Previous
          </button>
          <button
            onClick={handleNextTask}
            className="px-5 py-2 bg-gradient-to-r from-[#10b981] to-[#059669] text-[#1a1f2e] rounded-md text-[13px] font-semibold cursor-pointer transition-all hover:shadow-lg hover:scale-105"
          >
            Next Task
          </button>
        </div>
      </div>
    </div>
  );
}
